if (!customElements.get('store-social-proof')) {
  customElements.define('store-social-proof', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.items = this.querySelector('[data-proof-items]');
      if (!this.items) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.isRail = this.dataset.kind === 'videos' || this.dataset.layout === 'compact';
      this.previous = this.querySelector('[data-proof-previous]');
      this.next = this.querySelector('[data-proof-next]');
      this.progress = this.querySelector('[data-proof-progress] span');
      if (!this.isRail) {
        if (this.previous) this.previous.hidden = true;
        if (this.next) this.next.hidden = true;
        if (this.progress?.parentElement) this.progress.parentElement.hidden = true;
      }
      this.limit = Number(this.dataset.limit) || 6;
      this.pages = 1;
      this.batchSize = this.limit;
      if (this.dataset.kind === 'reviews') {
        this.dir = this.dataset.language === 'ar' ? 'rtl' : 'ltr';
        const viewport = matchMedia('(max-width: 749px)');
        const layout = () => {
          this.batchSize = viewport.matches ? Math.min(3, this.limit) : this.limit;
          this.visibleCount = this.pages * this.batchSize;
          if (this.more) this.update();
        };
        viewport.addEventListener('change', layout, options);
        layout();
      }
      this.visibleCount = this.batchSize;
      const configuredFilters = [...this.querySelectorAll('[data-proof-filter]')];
      this.filter = configuredFilters.some((button) => button.dataset.proofFilter === 'all') ? 'all' : (configuredFilters[0]?.dataset.proofFilter || 'all');
      this.sequence = 0;
      this.keys = new Set();
      this.autoplayBlocked = new WeakSet();
      this.nextCursor = null;
      this.more = this.querySelector('[data-proof-more]');
      this.status = this.querySelector('[data-proof-status]');
      this.empty = this.querySelector('[data-proof-empty]');
      this.dialog = this.querySelector('[data-proof-dialog]');
      this.player = this.querySelector('[data-proof-player]');
      this.browser = this.querySelector('[data-proof-browser]');
      this.browserItems = this.querySelector('[data-proof-browser-items]');
      this.browserStatus = this.querySelector('[data-proof-browser-status]');
      this.browserMore = this.querySelector('[data-proof-browser-more]');
      this.browserPlatform = this.querySelector('[data-proof-browser-platform]');
      this.browserSort = this.querySelector('[data-proof-browser-sort]');
      this.browserRating = this.querySelector('[data-proof-browser-rating] select');
      this.browserRatingWrap = this.querySelector('[data-proof-browser-rating]');
      this.browserCount = this.querySelector('[data-proof-browser-count]');
      this.words = this.dataset.language === 'ar'
        ? { more: 'قراءة المزيد', less: 'عرض أقل', source: 'المصدر الأصلي', play: 'تشغيل الفيديو', loading: 'جارٍ تحميل المحتوى…', error: 'تعذر تحديث المحتوى.', retry: 'إعادة المحاولة', product: 'عرض المنتج' }
        : this.dataset.language === 'fr'
          ? { more: 'Lire la suite', less: 'Réduire', source: 'Source originale', play: 'Lire la vidéo', loading: 'Chargement…', error: 'Impossible de mettre à jour le contenu.', retry: 'Réessayer', product: 'Voir le produit' }
          : { more: 'Read more', less: 'Show less', source: 'Original source', play: 'Play video', loading: 'Loading…', error: 'Content could not be updated.', retry: 'Retry', product: 'View product' };
      this.addEventListener('click', (event) => this.click(event), options);
      this.browser?.addEventListener('change', (event) => {
        if (!event.target.matches('select')) return;
        this.updateBrowserRating();
        this.loadBrowser(true);
      }, options);
      this.items.addEventListener('scroll', () => {
        cancelAnimationFrame(this.railFrame);
        this.railFrame = requestAnimationFrame(() => { this.updateRail(); this.scheduleInlineVideo(); });
      }, { ...options, passive: true });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopInlineVideo(); else this.scheduleInlineVideo();
      }, options);
      this.addEventListener('error', (event) => {
        const image = event.target;
        if (!image.matches?.('img.review-card__avatar')) return;
        const name = image.closest('[data-proof-card]').querySelector('.review-card__name')?.textContent || '';
        const fallback = this.node('span', 'review-card__avatar', [...name.trim()][0] || '•');
        fallback.setAttribute('aria-hidden', 'true');
        image.replaceWith(fallback);
      }, { ...options, capture: true });
      this.dialog.addEventListener('close', () => this.stopVideo(), options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }, options);
      this.browser?.addEventListener('close', () => {
        this.browserRequest?.abort();
        if (this.browserOpener?.isConnected) this.browserOpener.focus({ preventScroll: true });
        this.browserOpener = null;
      }, options);
      this.browser?.addEventListener('click', (event) => {
        if (event.target !== this.browser) return;
        const box = this.browser.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.browser.close();
      }, options);
      this.addEventListener('shopify:block:select', (event) => {
        const card = event.target.closest('[data-proof-card]');
        if (card) {
          this.filter = 'all';
          this.visibleCount = this.items.children.length;
          this.update();
        }
      }, options);
      this.resize = new ResizeObserver(() => this.measure());
      this.resize.observe(this.items);
      this.update();
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        if (this.dataset.endpoint) this.load(true);
      }, { rootMargin: '300px 0px' });
      this.observer.observe(this);
      this.observeVideoCards();
    }

    disconnectedCallback() {
      this.sequence++;
      this.request?.abort();
      this.events?.abort();
      this.observer?.disconnect();
      this.resize?.disconnect();
      this.videoObserver?.disconnect();
      clearTimeout(this.inlineTimer);
      cancelAnimationFrame(this.railFrame);
      this.stopInlineVideo();
      if (this.dialog?.open) this.dialog.close();
      if (this.browser?.open) this.browser.close();
      this.browserRequest?.abort();
      this.stopVideo();
      this.events = null;
    }

    click(event) {
      const filter = event.target.closest('[data-proof-filter]');
      if (filter && filter.closest('store-social-proof') === this) {
        this.filter = filter.dataset.proofFilter;
        this.pages = 1;
        this.visibleCount = this.batchSize;
        this.items.scrollTo({ left: 0, behavior: 'auto' });
        this.update();
        if (this.dataset.endpoint) this.load(true);
        return;
      }
      const expand = event.target.closest('[data-proof-expand]');
      if (expand) {
        const body = expand.closest('[data-proof-card]').querySelector('[data-proof-body]');
        if (expand.closest('[data-proof-browser-items]')) {
          const expanded = body.classList.toggle('is-expanded');
          expand.setAttribute('aria-expanded', String(expanded));
          expand.textContent = expanded ? this.words.less : this.words.more;
          return;
        }
        this.openReview(body, expand.closest('[data-proof-card]').querySelector('.review-card__name')?.textContent || '');
        return;
      }
      if (event.target.closest('[data-proof-browse]')) { this.openBrowser(event.target.closest('[data-proof-browse]')); return; }
      if (event.target.closest('[data-proof-browser-close]')) { this.browser.close(); return; }
      if (event.target.closest('[data-proof-browser-more]')) { this.loadBrowser(false); return; }
      if (event.target.closest('[data-proof-previous]')) { this.scrollRail(-1); return; }
      if (event.target.closest('[data-proof-next]')) { this.scrollRail(1); return; }
      if (event.target.closest('[data-proof-more]')) {
        this.pages++;
        this.visibleCount += this.batchSize;
        this.update();
        if (this.nextCursor && this.matchingCards().length < this.visibleCount) this.load(false);
        return;
      }
      if (event.target.closest('[data-proof-retry]')) { this.load(this.failedReset); return; }
      if (event.target.closest('[data-proof-close]')) { this.dialog.close(); return; }
      const play = event.target.closest('[data-proof-play]');
      if (!play || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button) return;
      const embed = this.embedUrl(play.href);
      if ((!embed && !play.dataset.proofVideo && !play.dataset.proofPlayback) || typeof this.dialog.showModal !== 'function') return;
      event.preventDefault();
      this.openVideo(play, embed);
    }

    async openVideo(play, embed) {
      this.stopInlineVideo();
      this.claimPlayback();
      document.querySelectorAll('store-social-proof dialog[open]').forEach((dialog) => dialog.close());
      this.querySelector('[data-proof-original]').href = play.href;
      this.opener = play;
      this.player.replaceChildren(this.node('p', 'social-proof__status', this.words.loading));
      this.dialog.showModal();
      this.playerRequest = new AbortController();
      const signal = this.playerRequest.signal;
      try {
        const native = await this.nativeVideoUrl(play, signal);
        if (signal.aborted || !this.dialog.open) return;
        if (native) {
          const video = this.createVideo(native, false);
          this.player.replaceChildren(video);
          video.play().catch(() => {});
          return;
        }
      } catch (error) { if (signal.aborted) return; }
      if (signal.aborted || !this.dialog.open) return;
      if (!embed || new URL(embed).hostname === 'www.instagram.com') {
        this.player.replaceChildren(this.node('p', 'social-proof__status', this.words.error));
        return;
      }
      const iframe = document.createElement('iframe');
      iframe.src = embed;
      iframe.title = play.getAttribute('aria-label') || this.words.play;
      iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      this.player.replaceChildren(iframe);
      this.playerObserver?.disconnect();
      this.playerObserver = this.fitEmbed(iframe, this.player);
    }

    stopVideo() {
      this.playerRequest?.abort();
      this.playerRequest = null;
      this.playerObserver?.disconnect();
      this.playerObserver = null;
      this.releaseVideos(this.player);
      this.player?.replaceChildren();
      if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      this.opener = null;
      if (this.isConnected) this.scheduleInlineVideo();
    }

    claimPlayback() {
      document.querySelectorAll('store-social-proof').forEach(section => {
        if (section !== this) section.stopInlineVideo();
      });
    }

    async nativeVideoUrl(cover, signal) {
      const resolver = this.safeUrl(cover.dataset.proofPlayback);
      const endpoint = this.safeUrl(this.dataset.endpoint);
      if (resolver && endpoint && resolver.origin === endpoint.origin) {
        const response = await fetch(resolver.href, { signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Video unavailable');
        const data = await response.json();
        return this.safeUrl(data.video_url)?.href || null;
      }
      return this.safeUrl(cover.dataset.proofVideo)?.href || null;
    }

    createVideo(src, inline) {
      const video = this.node('video', '');
      video.preload = 'none';
      video.playsInline = true;
      video.muted = video.defaultMuted = inline;
      video.loop = inline;
      video.controls = !inline;
      if (inline) video.dataset.proofInlinePlayer = '';
      video.src = src;
      return video;
    }

    releaseVideos(container) {
      container?.querySelectorAll('video').forEach(video => {
        video.pause();
        video.removeAttribute('src');
        video.load();
      });
    }

    fitEmbed(iframe, container) {
      const width = 400;
      const height = Math.ceil(width * 16 / 9);
      iframe.classList.add('social-proof__embed');
      iframe.style.setProperty('--proof-embed-width', `${width}px`);
      iframe.style.setProperty('--proof-embed-height', `${height}px`);
      const fit = () => {
        if (!container.clientWidth || !container.clientHeight) return;
        const scale = Math.min(container.clientWidth / width, container.clientHeight / height);
        iframe.style.setProperty('--proof-embed-scale', String(scale));
      };
      fit();
      const observer = new ResizeObserver(fit);
      observer.observe(container);
      return observer;
    }

    safeUrl(value, sameOrigin = false) {
      if (typeof value !== 'string' || !value.trim()) return null;
      try {
        const url = new URL(value, location.origin);
        const localHttp = sameOrigin && url.origin === location.origin && url.protocol === 'http:';
        if ((url.protocol !== 'https:' && !localHttp) || url.username || url.password) return null;
        if (sameOrigin && url.origin !== location.origin) return null;
        return url;
      } catch { return null; }
    }

    embedUrl(value, inline = false) {
      const url = this.safeUrl(value);
      if (!url) return null;
      const host = url.hostname.toLowerCase().replace(/^www\./, '');
      if (host === 'instagram.com') return null;
      if (host === 'tiktok.com' || host === 'm.tiktok.com') {
        const match = url.pathname.match(/^\/@[^/]+\/video\/(\d+)\/?$/);
        if (match) return `https://www.tiktok.com/player/v1/${match[1]}?autoplay=1&rel=0${inline ? '&muted=1&controls=0&loop=1&description=0&music_info=0' : ''}`;
      }
      if (host === 'facebook.com' || host === 'm.facebook.com' || host === 'web.facebook.com' || host === 'fb.watch') {
        return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url.href)}&show_text=false&autoplay=true${inline ? '&mute=1' : ''}`;
      }
      return null;
    }

    matchingCards() {
      return [...this.items.querySelectorAll('[data-proof-card]')].filter((card) => this.filter === 'all' || (this.filter === 'video' ? card.dataset.type === 'video' : (card.dataset.platforms || card.dataset.platform).split(',').includes(this.filter)));
    }

    update() {
      const cards = [...this.items.querySelectorAll('[data-proof-card]')];
      const matches = this.matchingCards();
      const visible = new Set((this.isRail ? matches : matches.slice(0, this.visibleCount)));
      cards.forEach((card) => { card.hidden = !visible.has(card); });
      this.dataset.videoView = String(this.filter === 'video');
      const filters = this.querySelector('.social-proof__filters');
      const filterButtons = [...filters.querySelectorAll('[data-proof-filter]')];
      filterButtons.forEach((button) => {
        const kind = button.dataset.proofFilter;
        button.hidden = !this.dataset.endpoint && kind !== 'all' && !cards.some((card) => kind === 'video' ? card.dataset.type === 'video' : (card.dataset.platforms || card.dataset.platform).split(',').includes(kind));
        button.setAttribute('aria-pressed', String(kind === this.filter));
      });
      filters.hidden = (cards.length === 0 && !this.dataset.endpoint) || !filterButtons.some((button) => !button.hidden);
      if (this.more) this.more.hidden = this.isRail || (matches.length <= this.visibleCount && !this.nextCursor);
      this.empty.hidden = visible.size > 0 || !!this.loading || (!!this.dataset.endpoint && !this.loaded);
      this.measure();
      this.updateRail();
      this.observeVideoCards();
    }

    measure() {
      this.items.querySelectorAll('[data-proof-body]').forEach((body) => {
        const button = body.closest('[data-proof-card]').querySelector('[data-proof-expand]');
        if (!button || body.closest('[hidden]')) return;
        button.hidden = !body.classList.contains('is-expanded') && body.scrollHeight <= body.clientHeight + 2;
      });
    }

    openReview(body, author) {
      if (!body?.textContent?.trim() || typeof HTMLDialogElement === 'undefined') return;
      if (!this.textDialog) {
        const dialog = this.node('dialog', 'review-card-dialog');
        const bar = this.node('div', 'review-card-dialog__bar');
        const title = this.node('strong', 'review-card-dialog__title');
        const close = this.node('button', 'review-card-dialog__close', '×');
        close.type = 'button'; close.setAttribute('aria-label', this.dataset.language === 'ar' ? 'إغلاق' : this.dataset.language === 'fr' ? 'Fermer' : 'Close');
        close.addEventListener('click', () => dialog.close(), { signal: this.events.signal });
        bar.append(title, close);
        const text = this.node('p', 'review-card-dialog__body notranslate');
        text.dir = 'auto'; text.setAttribute('translate', 'no');
        dialog.append(bar, text);
        dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); }, { signal: this.events.signal });
        this.append(dialog); this.textDialog = dialog;
      }
      this.textDialog.querySelector('.review-card-dialog__title').textContent = author;
      this.textDialog.querySelector('.review-card-dialog__body').textContent = body.textContent;
      this.textDialog.showModal();
    }

    openBrowser(opener) {
      if (!this.browser || typeof this.browser.showModal !== 'function') return;
      this.browserOpener = opener;
      this.browserPlatform.value = ['google','facebook','instagram','tiktok','video'].includes(this.filter) ? this.filter : 'all';
      this.updateBrowserRating();
      this.browser.showModal();
      this.loadBrowser(true);
    }

    updateBrowserRating() {
      if (!this.browserRatingWrap) return;
      const google = this.browserPlatform?.value === 'google';
      this.browserRatingWrap.hidden = !google;
      if (!google && this.browserRating) this.browserRating.value = 'all';
    }

    browserWords(count) {
      if (this.dataset.language === 'ar') return `${count} رأيًا معروضًا`;
      if (this.dataset.language === 'fr') return `${count} avis affichés`;
      return `${count} reviews shown`;
    }

    browserManualCards() {
      const platform = this.browserPlatform?.value || 'all';
      const rating = this.browserRating?.value || 'all';
      let cards = [...this.items.querySelectorAll('[data-proof-card]')].filter(card => {
        const platformMatch = platform === 'all' || (platform === 'video' ? card.dataset.type === 'video' : (card.dataset.platforms || card.dataset.platform || '').split(',').includes(platform));
        const ratingMatch = rating === 'all' || card.dataset.rating === rating;
        return platformMatch && ratingMatch;
      });
      if (this.browserSort?.value === 'oldest') cards.reverse();
      return cards.map(card => { const clone = card.cloneNode(true); clone.hidden = false; return clone; });
    }

    async loadBrowser(reset) {
      if (!this.browserItems) return;
      const endpoint = this.safeUrl(this.dataset.endpoint);
      if (!endpoint) {
        const cards = this.browserManualCards();
        this.browserItems.replaceChildren(...cards);
        this.browserCount.textContent = this.browserWords(cards.length);
        this.browserMore.hidden = true;
        this.measureBrowser();
        return;
      }
      this.browserRequest?.abort();
      const controller = new AbortController();
      this.browserRequest = controller;
      this.browserStatus.hidden = false;
      this.browserStatus.textContent = this.words.loading;
      this.browserMore.disabled = true;
      endpoint.searchParams.set('kind', 'reviews');
      endpoint.searchParams.set('limit', '24');
      endpoint.searchParams.set('filter', this.browserPlatform?.value || 'all');
      endpoint.searchParams.set('sort', this.browserSort?.value || 'newest');
      if (this.browserPlatform?.value === 'google' && this.browserRating?.value !== 'all') endpoint.searchParams.set('rating', this.browserRating.value);
      else endpoint.searchParams.delete('rating');
      if (!reset && this.browserCursor) endpoint.searchParams.set('cursor', this.browserCursor);
      else endpoint.searchParams.delete('cursor');
      try {
        const response = await fetch(endpoint, { signal: controller.signal, credentials: endpoint.origin === location.origin ? 'same-origin' : 'omit', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Feed unavailable');
        const data = await response.json();
        if (!Array.isArray(data.items)) throw new Error('Invalid feed');
        const fragment = document.createDocumentFragment();
        for (const item of data.items) { const card = this.makeCard(item); if (card) fragment.appendChild(card); }
        if (reset) this.browserItems.replaceChildren(fragment); else this.browserItems.appendChild(fragment);
        this.browserCursor = typeof data.next_cursor === 'string' ? data.next_cursor : null;
        this.browserMore.hidden = !this.browserCursor;
        this.browserCount.textContent = this.browserWords(this.browserItems.children.length);
        this.browserStatus.hidden = true;
        this.measureBrowser();
      } catch (error) {
        if (controller.signal.aborted) return;
        this.browserStatus.hidden = false;
        this.browserStatus.textContent = this.words.error;
      } finally { this.browserMore.disabled = false; }
    }

    measureBrowser() {
      requestAnimationFrame(() => this.browserItems?.querySelectorAll('[data-proof-body]').forEach(body => {
        const button = body.closest('[data-proof-card]')?.querySelector('[data-proof-expand]');
        if (button) button.hidden = body.scrollHeight <= body.clientHeight + 2;
      }));
    }

    railIndex() {
      const cards = this.matchingCards().filter(card => !card.hidden);
      if (!cards.length) return { cards, index: 0 };
      const box = this.items.getBoundingClientRect();
      const rtl = getComputedStyle(this.items).direction === 'rtl';
      let index = 0, distance = Infinity;
      cards.forEach((card, candidate) => {
        const rect = card.getBoundingClientRect();
        const value = Math.abs(rtl ? box.right - rect.right : rect.left - box.left);
        if (value < distance) { distance = value; index = candidate; }
      });
      return { cards, index };
    }

    scrollRail(delta) {
      if (!this.isRail) return;
      const { cards, index } = this.railIndex();
      const target = cards[Math.max(0, Math.min(cards.length - 1, index + delta))];
      if (target && target !== cards[index]) target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      else if (delta > 0 && this.nextCursor && !this.loading) this.load(false).then(() => this.scrollRail(1));
    }

    updateRail() {
      if (!this.isRail || !this.previous || !this.next || !this.progress) return;
      const { cards, index } = this.railIndex();
      const canMove = this.items.scrollWidth > this.items.clientWidth + 2;
      this.previous.hidden = this.next.hidden = !canMove;
      this.previous.disabled = index <= 0;
      this.next.disabled = index >= cards.length - 1 && !this.nextCursor;
      this.progress.style.setProperty('--proof-progress', cards.length ? `${((index + 1) / cards.length) * 100}%` : '0%');
      this.progress.parentElement.hidden = !canMove;
    }

    observeVideoCards() {
      this.videoObserver?.disconnect();
      this.videoRatios = new Map();
      if (!this.querySelector('[data-type="video"]') || matchMedia('(prefers-reduced-motion: reduce)').matches) { this.stopInlineVideo(); return; }
      this.videoObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => this.videoRatios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0));
        this.scheduleInlineVideo();
      }, { threshold: [0, .45, .68, .85] });
      this.querySelectorAll('[data-type="video"]:not([hidden])').forEach(card => this.videoObserver.observe(card));
    }

    scheduleInlineVideo() {
      clearTimeout(this.inlineTimer);
      this.inlineTimer = setTimeout(() => this.updateInlineVideo(), 160);
    }

    async updateInlineVideo() {
      if (document.hidden || !this.isConnected || document.querySelector('store-social-proof dialog[open]') || this.textDialog?.open) { this.stopInlineVideo(); return; }
      const center = innerWidth / 2;
      const candidates = [...(this.videoRatios || new Map())].filter(([card, ratio]) => ratio >= .68 && !card.hidden && !this.autoplayBlocked.has(card));
      candidates.sort((a, b) => b[1] - a[1] || Math.abs((a[0].getBoundingClientRect().left + a[0].getBoundingClientRect().right) / 2 - center) - Math.abs((b[0].getBoundingClientRect().left + b[0].getBoundingClientRect().right) / 2 - center));
      const card = candidates[0]?.[0];
      if (!card) { this.stopInlineVideo(); return; }
      if (card === this.inlineCard) return;
      this.stopInlineVideo();
      this.claimPlayback();
      const cover = card.querySelector('[data-proof-play]');
      this.inlineCard = card;
      this.inlineRequest = new AbortController();
      const signal = this.inlineRequest.signal;
      try {
        const native = await this.nativeVideoUrl(cover, signal);
        if (signal.aborted || this.inlineCard !== card) return;
        if (native) {
          const video = this.createVideo(native, true);
          video.addEventListener('playing', () => {
            if (this.inlineCard === card) card.classList.add('is-playing');
          }, { once: true, signal });
          video.addEventListener('error', () => {
            this.autoplayBlocked.add(card);
            if (this.inlineCard === card) this.stopInlineVideo();
          }, { once: true, signal });
          cover.append(video);
          await video.play();
          return;
        }
      } catch (error) {
        if (signal.aborted) return;
        this.autoplayBlocked.add(card);
        if (this.inlineCard === card) this.stopInlineVideo();
        return;
      }
      const src = this.embedUrl(cover?.href, true);
      if (!src || new URL(src).hostname === 'www.instagram.com') {
        this.autoplayBlocked.add(card);
        this.stopInlineVideo();
        return;
      }
      const iframe = document.createElement('iframe');
      iframe.dataset.proofInlinePlayer = '';
      iframe.src = src; iframe.title = cover.getAttribute('aria-label') || this.words.play;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture'; iframe.tabIndex = -1;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      cover.append(iframe);
      this.inlinePlayerObserver = this.fitEmbed(iframe, cover);
      card.classList.add('is-playing'); this.inlineCard = card;
    }

    stopInlineVideo() {
      this.inlineRequest?.abort();
      this.inlineRequest = null;
      this.inlinePlayerObserver?.disconnect();
      this.inlinePlayerObserver = null;
      this.releaseVideos(this.items);
      this.querySelectorAll('[data-proof-inline-player]').forEach(player => player.remove());
      this.querySelectorAll('.is-playing').forEach(card => card.classList.remove('is-playing'));
      this.inlineCard = null;
    }

    // Feed contract: { items: [{ id, type, platform, author, text, rating,
    // source_url, source_type, avatar_url, cover_url, title, published_at,
    // product: { title, url, image_url, price_label } }], next_cursor: null|string }.
    // The authenticated sync service owns platform tokens; no tokens belong in theme settings.
    async load(reset) {
      const endpoint = this.safeUrl(this.dataset.endpoint);
      if (!endpoint) { this.fail(reset); return; }
      const sequence = ++this.sequence;
      this.request?.abort();
      const controller = new AbortController();
      this.request = controller;
      const timeout = setTimeout(() => controller.abort(), 15000);
      this.loading = true;
      if (this.more) this.more.disabled = true;
      this.items.setAttribute('aria-busy', 'true');
      this.status.hidden = false;
      this.status.textContent = this.words.loading;
      endpoint.searchParams.set('kind', this.dataset.kind);
      endpoint.searchParams.set('limit', '24');
      endpoint.searchParams.set('filter', this.filter);
      if (!reset && this.nextCursor) endpoint.searchParams.set('cursor', this.nextCursor);
      else endpoint.searchParams.delete('cursor');
      try {
        const response = await fetch(endpoint, { signal: controller.signal, credentials: endpoint.origin === location.origin ? 'same-origin' : 'omit', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Feed unavailable');
        const data = await response.json();
        if (!Array.isArray(data.items)) throw new Error('Invalid feed');
        if (sequence !== this.sequence || !this.isConnected) return;
        const fragment = document.createDocumentFragment();
        const keys = reset ? new Set() : new Set(this.keys);
        for (const item of data.items) {
          if (!item || item.id == null) continue;
          const key = `${item.platform}:${item.id}`;
          if (keys.has(key)) continue;
          const card = this.makeCard(item);
          if (card) { fragment.appendChild(card); keys.add(key); }
        }
        if (reset) this.items.replaceChildren(fragment);
        else this.items.appendChild(fragment);
        this.keys = keys;
        this.nextCursor = typeof data.next_cursor === 'string' && (reset || data.next_cursor !== this.nextCursor) ? data.next_cursor : null;
        this.loaded = true;
        this.status.hidden = true;
      } catch {
        if (sequence === this.sequence && this.isConnected) this.fail(reset);
      } finally {
        clearTimeout(timeout);
        if (sequence === this.sequence) {
          this.loading = false;
          if (this.more) this.more.disabled = false;
          this.items.setAttribute('aria-busy', 'false');
          this.update();
        }
      }
    }

    fail(reset) {
      this.failedReset = reset;
      this.status.hidden = false;
      this.status.textContent = this.words.error;
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.dataset.proofRetry = '';
      retry.textContent = this.words.retry;
      this.status.appendChild(retry);
    }

    node(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text != null) node.textContent = String(text);
      return node;
    }

    makeImage(value, className, size) {
      const url = this.safeUrl(value);
      if (!url) return null;
      const image = this.node('img', className);
      image.loading = 'lazy';
      image.decoding = 'async';
      image.referrerPolicy = 'no-referrer';
      image.alt = '';
      if (size) image.width = image.height = size;
      image.src = url.href;
      return image;
    }

    makeSource(url, type, compact = false) {
      let label = this.words.source;
      if (type === 'profile') label = this.dataset.language === 'ar' ? 'عرض صفحة النشاط' : this.dataset.language === 'fr' ? 'Voir la fiche' : 'View business profile';
      else if (this.dataset.language === 'ar') label = type === 'comment' ? 'عرض التعليق' : type === 'review' ? 'عرض المراجعة' : 'عرض المنشور';
      else if (this.dataset.language === 'fr') label = type === 'comment' ? 'Voir le commentaire' : type === 'review' ? 'Voir l’avis' : 'Voir la publication';
      else label = type === 'comment' ? 'View comment' : type === 'review' ? 'View review' : 'View post';
      const link = this.node('a', compact ? 'review-card__source' : 'social-proof__source', label);
      const arrow = this.node('span', compact ? 'review-card__source-arrow' : '', '↗');
      arrow.setAttribute('aria-hidden', 'true');
      link.append(arrow);
      link.href = url.href;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      return link;
    }


    platformIcon(platform){
      const svg=document.createElementNS('http://www.w3.org/2000/svg','svg');svg.setAttribute('viewBox','0 0 24 24');svg.setAttribute('aria-hidden','true');svg.setAttribute('width','18');svg.setAttribute('height','18');
      if (platform === 'google') {
        const paths = [
          ['#4285F4', 'M21.6 12.23c0-.71-.06-1.39-.18-2.05H12v3.88h5.38a4.6 4.6 0 0 1-2 3.02v2.51h3.24c1.89-1.74 2.98-4.3 2.98-7.36Z'],
          ['#34A853', 'M12 22c2.7 0 4.96-.9 6.62-2.41l-3.24-2.51c-.9.6-2.04.96-3.38.96-2.6 0-4.8-1.76-5.59-4.12H3.07v2.59A10 10 0 0 0 12 22Z'],
          ['#FBBC05', 'M6.41 13.92a6 6 0 0 1 0-3.84V7.49H3.07a10 10 0 0 0 0 9.02l3.34-2.59Z'],
          ['#EA4335', 'M12 5.96c1.47 0 2.79.51 3.82 1.51l2.87-2.87A9.6 9.6 0 0 0 12 2a10 10 0 0 0-8.93 5.49l3.34 2.59A5.99 5.99 0 0 1 12 5.96Z']
        ];
        for (const [color, data] of paths) {
          const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path.setAttribute('d', data); path.setAttribute('fill', color); svg.appendChild(path);
        }
        return svg;
      }
      const paths={facebook:'M13.5 22v-8h2.8l.4-3h-3.2V9c0-.9.3-1.5 1.6-1.5H17V4.8c-.3 0-1.4-.1-2.6-.1-2.6 0-4.3 1.6-4.3 4.5V11H7.3v3h2.8v8z',instagram:'M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3zm5 3a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm5.5-3a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3z',tiktok:'M15 2h3c.3 2.2 1.7 3.8 4 4v3c-1.5 0-2.8-.4-4-1.2V16a6 6 0 1 1-6-6v3a3 3 0 1 0 3 3z'};
      const path=document.createElementNS('http://www.w3.org/2000/svg','path');path.setAttribute('d',paths[platform]);path.setAttribute('fill','currentColor');svg.appendChild(path);return svg;
    }

    makeCard(item) {
      const platforms = { google: 'Google', facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok' };
      if (!platforms[item.platform]) return null;
      const url = this.safeUrl(item.source_url);
      if (!url) return null;
      const isVideo = item.type === 'video';
      if (this.dataset.kind === 'videos' && !isVideo) return null;
      const hasRating = item.platform === 'google' && Number.isInteger(Number(item.rating)) && Number(item.rating) >= 1 && Number(item.rating) <= 5;
      if (!isVideo && !hasRating && (typeof item.text !== 'string' || !item.text.trim())) return null;
      const card = this.node('article', isVideo ? 'social-proof__video' : 'social-proof__review review-card');
      card.dataset.proofCard = '';
      card.dataset.platform = item.platform;
      if(Array.isArray(item.platforms))card.dataset.platforms=item.platforms.join(',');
      card.dataset.type = isVideo ? 'video' : 'text';
      if (!isVideo && hasRating) card.dataset.rating = String(Number(item.rating));
      if (isVideo) {
        const cover = this.node('a', 'social-proof__cover');
        cover.href = url.href;
        cover.target = '_blank';
        cover.rel = 'noopener noreferrer';
        cover.dataset.proofPlay = '';
        if (item.video_url) cover.dataset.proofVideo = item.video_url;
        if (item.playback_url) cover.dataset.proofPlayback = item.playback_url;
        cover.setAttribute('aria-label', `${this.words.play}: ${item.title || ''}`);
        const image = this.makeImage(item.cover_url);
        if (image) cover.appendChild(image);
        const play = this.node('span', 'social-proof__play', '▶');
        play.setAttribute('aria-hidden', 'true');
        const muted = this.node('span', 'social-proof__muted', '×♪');
        muted.setAttribute('aria-hidden', 'true');
        cover.append(play, muted);
        const sources=Array.isArray(item.sources)?item.sources:[{platform:item.platform,url:item.source_url}];
        const caption = this.node('div', 'social-proof__video-caption');
        if (item.title) { const title = this.node('h3', 'notranslate', item.title); title.dir = 'auto'; title.setAttribute('translate', 'no'); caption.appendChild(title); }
        if(item.description){const body=this.node('p','social-proof__video-description notranslate',item.description);body.dir='auto';body.setAttribute('translate','no');caption.append(body);}
        if (item.author) caption.appendChild(this.node('small', '', item.author));
        const product = item.product;
        let productUrl = this.safeUrl(product?.url, true);
        if (productUrl && this.dataset.rootUrl) {
          const root = new URL(this.dataset.rootUrl, location.origin);
          const handle = productUrl.pathname.match(/\/products\/([^/]+)\/?$/)?.[1];
          if (root.origin === location.origin && root.pathname.endsWith('/') && handle) productUrl = new URL(`products/${handle}${productUrl.search}${productUrl.hash}`, root);
        }
        if (productUrl && product.title) {
          const link = this.node('a', 'social-proof__product');
          link.href = productUrl.href;
          const productImage = this.makeImage(product.image_url, '', 44);
          if (productImage) link.appendChild(productImage);
          const title = this.node('span');
          title.appendChild(this.node('span', 'social-proof__product-title', product.title));
          if (product.price_label) title.appendChild(this.node('strong', '', product.price_label));
          const action = this.node('b', '', this.words.product);
          link.append(title, action);
          caption.appendChild(link);
        }
        const platformLinks = this.node('div', 'social-proof__platform-links');
        for (const source of sources) {
          const sourceUrl = this.safeUrl(source.url);
          if (!sourceUrl || !platforms[source.platform]) continue;
          const link = this.node('a', ''); link.href = sourceUrl.href; link.target = '_blank'; link.rel = 'noopener noreferrer';
          link.setAttribute('aria-label', `${this.words.source} · ${platforms[source.platform]}`);
          link.append(this.platformIcon(source.platform), this.node('span', '', platforms[source.platform]), this.node('i', '', '↗'));
          platformLinks.append(link);
        }
        caption.appendChild(platformLinks);
        card.append(cover,caption);
      } else {
        const header = this.node('header', 'review-card__header');
        const avatar = this.makeImage(item.avatar_url, 'review-card__avatar', 30) || this.node('span', 'review-card__avatar', [...(item.author || '').trim()][0] || '•');
        if (avatar.tagName !== 'IMG') avatar.setAttribute('aria-hidden', 'true');
        header.appendChild(avatar);
        const person = this.node('div', 'review-card__person');
        const name = this.node('bdi', 'review-card__name notranslate', item.author || '');
        name.dir = 'auto'; name.setAttribute('translate', 'no'); name.title = item.author || '';
        person.appendChild(name);
        const meta = this.node('div', 'review-card__meta');
        const rating = Number(item.rating);
        if (hasRating) {
          const stars = this.node('span', 'review-card__stars');
          stars.setAttribute('role', 'img'); stars.setAttribute('aria-label', `${rating} / 5`);
          const symbols = this.node('span', '', '★'.repeat(rating) + '☆'.repeat(5 - rating));
          symbols.setAttribute('aria-hidden', 'true'); stars.appendChild(symbols); meta.appendChild(stars);
        }
        const date = new Date(item.published_at);
        if (item.published_at && !Number.isNaN(date.getTime())) {
          const time = this.node('time', 'review-card__date', date.toLocaleDateString(this.dataset.language || 'en'));
          time.dateTime = date.toISOString();
          meta.appendChild(time);
        }
        person.appendChild(meta);
        const badge = this.node('span', 'review-card__platform');
        badge.dataset.platform = item.platform; badge.setAttribute('role', 'img'); badge.setAttribute('aria-label', platforms[item.platform]);
        badge.appendChild(this.platformIcon(item.platform));
        header.append(person, badge);
        card.appendChild(header);
        const body = this.node('p', 'review-card__body notranslate', item.text || '');
        body.dir = 'auto'; body.setAttribute('translate', 'no');
        body.dataset.proofBody = '';
        const expand = this.node('button', 'review-card__expand', this.words.more);
        expand.type = 'button';
        expand.dataset.proofExpand = '';
        expand.setAttribute('aria-expanded', 'false');
        expand.hidden = true;
        const footer = this.node('div', 'review-card__footer');
        footer.append(expand, this.makeSource(url, item.source_type, true));
        card.append(body, footer);
      }
      return card;
    }
  });
}
